/**
 * Child Progress Page — Kid-Friendly Redesign
 * Warm, colorful progress view with mascot, celebrations, and playful visuals
 */

import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { BookOpen, Flame, Star } from 'lucide-react';
import { SUBJECT_DISPLAY_NAMES, SUBJECT_ICONS, MASTERY_DISPLAY_NAMES } from '@kids-lms/constants';
import type { Subject, ChildActivitySummary } from '@kids-lms/types';
import { useChildStore, useSoundEffects, useCelebration } from '../hooks';
import { chatService, analyticsService, type ProgressSummary } from '../services';
import { springs, staggerContainer, staggerItem } from '../animation';
import MindoraCharacter from '../components/character/MindoraCharacter';
import SpeechBubble from '../components/ui/SpeechBubble';
import TornPaperCard from '../components/ui/TornPaperCard';
import LiquidProgressBar from '../components/ui/LiquidProgressBar';
import AnimatedNumber from '../components/ui/AnimatedNumber';
import StickerBadge from '../components/ui/StickerBadge';
import KidLoader from '../components/ui/KidLoader';
import { SparkleCluster, WavyDivider, DecorativeStar, DecorativeCloud } from '../components/decorations';
import BadgeReveal from '../components/effects/BadgeReveal';
import { kidPalette } from '../styles/kidPalette';

const masteryColors: Record<string, string> = {
  beginner: kidPalette.dandelion,
  developing: kidPalette.ocean,
  proficient: kidPalette.meadow,
  advanced: kidPalette.violet,
};


export default function ChildProgressPage() {
  const { selectedChild, streakData, fetchHabits } = useChildStore();
  const [progressData, setProgressData] = useState<ProgressSummary | null>(null);
  const [activityData, setActivityData] = useState<ChildActivitySummary | null>(null);
  const [dateRange, setDateRange] = useState<'week' | 'month' | 'all'>('week');
  const [loading, setLoading] = useState(true);
  const [hasSeenAchievements, setHasSeenAchievements] = useState(false);
  const { play } = useSoundEffects();
  const { celebrate } = useCelebration();
  const achievementsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!selectedChild?.id) return;

    const load = async () => {
      setLoading(true);
      try {
        const [progress] = await Promise.all([
          chatService.getProgressSummary(selectedChild.id),
          fetchHabits(selectedChild.id),
        ]);
        setProgressData(progress);

        let startDate: string | undefined;
        const now = new Date();
        if (dateRange === 'week') {
          startDate = new Date(now.getTime() - 7 * 86400000).toISOString().split('T')[0];
        } else if (dateRange === 'month') {
          startDate = new Date(now.getTime() - 30 * 86400000).toISOString().split('T')[0];
        }
        const activity = await analyticsService.getChildSummary(selectedChild.id, startDate);
        setActivityData(activity);
      } catch {
        // silent
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [selectedChild?.id, dateRange, fetchHabits]);

  // Trigger celebration when viewing new achievements
  useEffect(() => {
    if (!hasSeenAchievements && !loading && progressData) {
      const earnedCount = achievements(progressData, activityData, streakData).filter(a => a.earned).length;
      if (earnedCount > 0) {
        setHasSeenAchievements(true);
        // Small celebration for viewing progress
        const timer = setTimeout(() => celebrate('task'), 800);
        return () => clearTimeout(timer);
      }
    }
  }, [loading, progressData, activityData, streakData, hasSeenAchievements, celebrate]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <KidLoader message="Loading your awesome progress..." />
      </div>
    );
  }

  const name = selectedChild?.name || 'Friend';

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 pb-24 relative">
      {/* Background decorations */}
      <DecorativeCloud size={80} className="absolute top-12 right-4 opacity-20" />
      <DecorativeCloud size={60} className="absolute top-40 left-2 opacity-15" variant="wispy" />
      <DecorativeStar size={20} color={kidPalette.dandelion} pulse className="absolute top-20 left-8" />
      <DecorativeStar size={14} color={kidPalette.berry} pulse className="absolute top-56 right-12" />

      {/* Page Header with Mascot */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={springs.gentle}
        className="text-center mb-6 relative"
      >
        <div className="flex items-center justify-center gap-3 mb-2">
          <MindoraCharacter mood="celebrating" size="lg" />
          <SparkleCluster count={6} spread={30} />
        </div>
        <h1 className="font-display text-kid-3xl text-kid-text">
          {name}'s Progress
        </h1>
        <SpeechBubble className="max-w-xs mx-auto mt-3">
          <p className="text-kid-text-soft font-kid text-kid-sm text-center">
            Look how much you've learned! You're amazing!
          </p>
        </SpeechBubble>
      </motion.div>

      <WavyDivider color={kidPalette.ocean} className="mb-6" />

      {/* Stats Overview — Sticker Badge style */}
      <motion.div
        variants={staggerContainer()}
        initial="initial"
        animate="animate"
        className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6"
      >
        {[
          { icon: <BookOpen className="w-5 h-5" />, value: progressData?.totalLessonsCompleted || 0, label: 'Lessons', color: kidPalette.ocean, bg: 'bg-kid-ocean/10' },
          { icon: <span className="text-lg">📝</span>, value: progressData?.totalHomeworkSessions || 0, label: 'Homework', color: kidPalette.violet, bg: 'bg-kid-violet/10' },
          { icon: <Flame className="w-5 h-5" />, value: streakData?.habitStreak || 0, label: 'Day Streak', color: kidPalette.tangerine, bg: 'bg-kid-tangerine/10' },
          { icon: <Star className="w-5 h-5" />, value: streakData?.habitPoints || 0, label: 'Points', color: kidPalette.dandelion, bg: 'bg-kid-dandelion/10' },
        ].map((stat) => (
          <motion.div key={stat.label} variants={staggerItem}>
            <TornPaperCard className="text-center">
              <div
                className={`inline-flex items-center justify-center w-11 h-11 rounded-full mb-2 ${stat.bg}`}
                style={{ color: stat.color }}
              >
                {stat.icon}
              </div>
              <p className="font-display text-kid-2xl text-kid-text">
                <AnimatedNumber value={stat.value} />
              </p>
              <p className="text-kid-xs text-kid-text-soft font-kid font-bold">{stat.label}</p>
            </TornPaperCard>
          </motion.div>
        ))}
      </motion.div>

      {/* Subject Mastery */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ ...springs.gentle, delay: 0.1 }}
      >
        <TornPaperCard className="mb-6">
          <h2 className="font-display text-kid-xl text-kid-text mb-4 flex items-center gap-2">
            <span className="text-xl">🌟</span> Subject Mastery
          </h2>
          {progressData?.subjectProgress && progressData.subjectProgress.length > 0 ? (
            <div className="space-y-4">
              {progressData.subjectProgress.map((sp, i) => (
                <motion.div
                  key={sp.subject}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ ...springs.gentle, delay: 0.12 + i * 0.04 }}
                >
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">{SUBJECT_ICONS[sp.subject as Subject] || '📚'}</span>
                      <span className="font-kid font-bold text-kid-sm text-kid-text">
                        {SUBJECT_DISPLAY_NAMES[sp.subject as Subject] || sp.subject}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      {sp.avgScore !== null && (
                        <span className="font-kid font-bold text-kid-xs text-kid-text-soft">
                          {sp.avgScore}%
                        </span>
                      )}
                      <StickerBadge
                        color={masteryColors[sp.highestMastery] || kidPalette.textSoft}
                        rotation={-2}
                        className="!p-1.5 !text-[10px]"
                      >
                        {MASTERY_DISPLAY_NAMES[sp.highestMastery as keyof typeof MASTERY_DISPLAY_NAMES] || sp.highestMastery}
                      </StickerBadge>
                    </div>
                  </div>
                  <LiquidProgressBar
                    percentage={sp.avgScore || 25}
                    color={masteryColors[sp.highestMastery] || kidPalette.textSoft}
                    glow
                  />
                  {sp.lastActivity && (
                    <p className="text-kid-xs text-kid-text-soft mt-1 font-kid">
                      Last studied: {new Date(sp.lastActivity).toLocaleDateString()}
                    </p>
                  )}
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <MindoraCharacter mood="encouraging" size="md" />
              <p className="text-kid-text-soft font-kid text-kid-sm mt-3">
                Complete some lessons to see your mastery here!
              </p>
            </div>
          )}
        </TornPaperCard>
      </motion.div>

      {/* Activity Section */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ ...springs.gentle, delay: 0.15 }}
      >
        <TornPaperCard className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-kid-xl text-kid-text flex items-center gap-2">
              <span className="text-xl">📅</span> Activity
            </h2>
            <div className="flex gap-1">
              {(['week', 'month', 'all'] as const).map((range) => (
                <button
                  key={range}
                  onClick={() => { setDateRange(range); play('tap'); }}
                  className={`px-3 py-1.5 rounded-kid-sm font-kid text-kid-xs font-bold transition-colors touch-target ${
                    dateRange === range
                      ? 'bg-kid-ocean text-white'
                      : 'bg-kid-bg text-kid-text-soft hover:bg-kid-ocean/10'
                  }`}
                >
                  {range === 'week' ? '7d' : range === 'month' ? '30d' : 'All'}
                </button>
              ))}
            </div>
          </div>

          {/* Activity stats */}
          <div className="grid grid-cols-3 gap-3 mb-4">
            <div className="bg-kid-ocean/10 rounded-kid-sm p-3 text-center">
              <p className="font-display text-kid-lg text-kid-ocean">
                <AnimatedNumber value={activityData?.conversations.total || 0} />
              </p>
              <p className="text-kid-xs text-kid-text-soft font-kid font-bold">Chats</p>
            </div>
            <div className="bg-kid-meadow/10 rounded-kid-sm p-3 text-center">
              <p className="font-display text-kid-lg text-kid-meadow">
                <AnimatedNumber value={activityData?.conversations.totalMessages || 0} />
              </p>
              <p className="text-kid-xs text-kid-text-soft font-kid font-bold">Questions</p>
            </div>
            <div className="bg-kid-violet/10 rounded-kid-sm p-3 text-center">
              <p className="font-display text-kid-lg text-kid-violet">
                <AnimatedNumber value={`${Math.round(activityData?.conversations.avgSessionMinutes || 0)}m`} />
              </p>
              <p className="text-kid-xs text-kid-text-soft font-kid font-bold">Avg Session</p>
            </div>
          </div>

          {/* Daily activity bars */}
          {activityData?.dailyActivity && activityData.dailyActivity.length > 0 ? (
            <div className="space-y-1.5">
              {activityData.dailyActivity.slice(-7).map((day) => {
                const maxConvos = Math.max(...activityData.dailyActivity.map((d) => d.conversations), 1);
                const pct = Math.round((day.conversations / maxConvos) * 100);
                return (
                  <div key={day.date} className="flex items-center gap-3">
                    <span className="text-kid-xs text-kid-text-soft w-16 flex-shrink-0 font-kid">
                      {new Date(day.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                    </span>
                    <div className="flex-1">
                      <LiquidProgressBar
                        percentage={pct}
                        color={kidPalette.ocean}
                        height="h-5"
                      />
                    </div>
                    {day.conversations > 0 && (
                      <span className="text-kid-xs text-kid-ocean font-bold font-kid w-6 text-right">
                        {day.conversations}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-center text-kid-text-soft font-kid text-kid-sm py-4">
              No activity yet this period
            </p>
          )}
        </TornPaperCard>
      </motion.div>

      {/* Subject Breakdown */}
      {activityData?.subjects && activityData.subjects.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ ...springs.gentle, delay: 0.2 }}
        >
          <TornPaperCard className="mb-6">
            <h2 className="font-display text-kid-xl text-kid-text mb-4 flex items-center gap-2">
              <span className="text-xl">📚</span> Subjects Explored
            </h2>
            <div className="space-y-3">
              {activityData.subjects.map((s) => {
                const total = activityData.subjects.reduce((sum, sub) => sum + sub.count, 0);
                const pct = total > 0 ? Math.round((s.count / total) * 100) : 0;
                return (
                  <div key={s.subject}>
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <span className="text-base">{SUBJECT_ICONS[s.subject as Subject] || '📚'}</span>
                        <span className="font-kid font-semibold text-kid-sm text-kid-text capitalize">
                          {SUBJECT_DISPLAY_NAMES[s.subject as Subject] || s.subject}
                        </span>
                      </div>
                      <span className="text-kid-xs text-kid-text-soft font-kid">
                        {s.count} chats ({pct}%)
                      </span>
                    </div>
                    <LiquidProgressBar
                      percentage={pct}
                      color={kidPalette.violet}
                    />
                  </div>
                );
              })}
            </div>
          </TornPaperCard>
        </motion.div>
      )}

      {/* Achievements with BadgeReveal */}
      <motion.div
        ref={achievementsRef}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ ...springs.gentle, delay: 0.25 }}
        className="relative"
      >
        <SparkleCluster count={8} spread={50} className="absolute -top-4 right-4" />
        <TornPaperCard>
          <h2 className="font-display text-kid-xl text-kid-text mb-3 flex items-center gap-2">
            <span className="text-xl">🏆</span> Achievements
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {achievements(progressData, activityData, streakData).map((a, i) => (
              <motion.div
                key={a.label}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ ...springs.bouncy, delay: 0.3 + i * 0.06 }}
                className="relative"
              >
                <BadgeReveal trigger={a.earned}>
                  <div className={`text-center p-3 rounded-kid-sm ${
                    a.earned
                      ? 'bg-kid-bg shadow-kid'
                      : 'bg-kid-bg/50 opacity-40 grayscale'
                  }`}>
                    <span className="text-2xl block mb-1">{a.emoji}</span>
                    <p className={`text-kid-xs font-bold font-kid ${a.earned ? 'text-kid-text' : 'text-kid-text-soft'}`}>
                      {a.label}
                    </p>
                    <span className={`text-[10px] font-bold font-kid ${a.earned ? 'text-kid-meadow' : 'text-kid-text-soft'}`}>
                      {a.earned ? 'Earned!' : 'Keep going!'}
                    </span>
                  </div>
                </BadgeReveal>
              </motion.div>
            ))}
          </div>
        </TornPaperCard>
      </motion.div>
    </div>
  );
}

function achievements(
  progressData: ProgressSummary | null,
  activityData: ChildActivitySummary | null,
  streakData: any
) {
  return [
    {
      emoji: '💬',
      label: 'First Chat',
      earned: (activityData?.conversations.total || 0) >= 1,
    },
    {
      emoji: '📖',
      label: 'First Lesson',
      earned: (progressData?.totalLessonsCompleted || 0) >= 1,
    },
    {
      emoji: '🔥',
      label: '3-Day Streak',
      earned: (streakData?.habitStreak || 0) >= 3,
    },
    {
      emoji: '🧠',
      label: '3 Subjects',
      earned: (progressData?.subjectProgress?.length || 0) >= 3,
    },
    {
      emoji: '📝',
      label: 'Homework Hero',
      earned: (progressData?.totalHomeworkSessions || 0) >= 5,
    },
    {
      emoji: '🌟',
      label: '50 Points',
      earned: (streakData?.habitPoints || 0) >= 50,
    },
    {
      emoji: '🏅',
      label: 'Proficient',
      earned: progressData?.subjectProgress?.some((sp) => sp.highestMastery === 'proficient' || sp.highestMastery === 'advanced') || false,
    },
    {
      emoji: '🔥',
      label: '7-Day Streak',
      earned: (streakData?.habitStreak || 0) >= 7,
    },
  ];
}
